import { safeCancelable, SafePromise, SafePromiseCancelable, safeTimeout } from '@benzinga/safe-await';
import { Subscribable, Subscription } from '@benzinga/subscribable';
import { AutocompleteItem } from './entities';
import { Session } from '@benzinga/session';
import { AutocompleteRequest, AutocompleteRequestEvent } from './request';
import { calculateFundAutocomplete, calculateSymbolAutocomplete } from './utils';
import { debounce, DebounceClosure } from '@benzinga/utils';

export interface AutocompleteLoadResultsEvent {
  searchTerm: string;
  items: AutocompleteItem[];
  type: 'autocomplete_load_results';
}

export type AutocompleteMessengerEvent = AutocompleteRequestEvent | AutocompleteLoadResultsEvent;

/**
 *  Responsible for requests handling, autocomplete items list generation and making
 *  'autocomplete_load_results' call containing items list
 * @remarks AutocompleteMessenger is not shared among multiple Autocomplete components
 */
export class AutocompleteMessenger extends Subscribable<AutocompleteMessengerEvent> {
  public request: AutocompleteRequest;
  public searchDebounced: DebounceClosure<AutocompleteMessenger['search']>;

  private requestSubscription?: Subscription<AutocompleteRequest>;
  private cancelSearches: VoidFunction[];

  constructor(session: Session) {
    super();
    this.request = new AutocompleteRequest(session);
    this.cancelSearches = [];
    this.searchDebounced = debounce(this.search, 200);
  }

  /**
   *
   * Takes the current search term and a list of symbols(and funds) returned from server
   * autocomplete and creates list for dropdown.
   * Note that the API endpoint ensures that exact matches will always display first.
   *
   * @param allowFunds - determines if funds should be displayed
   * @param allowStocks - determines if stocks should be displayed
   * @param searchTerm - input query
   */
  public search = async (
    searchTerm: string,
    allowFunds: boolean | null | undefined,
    allowStocks: boolean | null | undefined,
    cancelPreviousSearch = false,
    limit = 100,
  ): SafePromiseCancelable<AutocompleteItem[]> => {
    cancelPreviousSearch && this.cancelPendingSearch();
    const items: AutocompleteItem[] = [];
    const searchValue = searchTerm.trim();
    if (searchValue) {
      const [symbolRes, fundRes] = await Promise.all([
        allowStocks ? this.makeCancelable(this.request.getAutoCompleteSymbols(searchValue, limit)) : null,
        allowFunds ? this.makeCancelable(this.request.getAutoCompleteFunds(searchValue, limit)) : null,
      ]);

      if (symbolRes && !symbolRes.canceled) {
        if (symbolRes.ok) {
          items.push(...calculateSymbolAutocomplete(symbolRes.ok.result));
        }
      }

      if (fundRes && !fundRes.canceled) {
        if (fundRes.ok) {
          items.push(...calculateFundAutocomplete(fundRes.ok.funds));
        }
      }

      if ((fundRes && fundRes.canceled) || (symbolRes && symbolRes.canceled)) {
        return { canceled: true };
      }
    }

    if (items.length) {
      this.dispatch({
        items,
        searchTerm,
        type: 'autocomplete_load_results',
      });
    }

    return { canceled: false, ok: items };
  };

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
    this.cancelPendingSearch();
  }

  private cancelPendingSearch = (): void => {
    this.cancelSearches.forEach(cancelSearch => {
      cancelSearch();
    });
    this.cancelSearches = [];
  };

  private makeCancelable<T>(request: SafePromise<T>, timeout = 2000): SafePromiseCancelable<T> {
    const { cancel, promise } = safeCancelable(safeTimeout(request, timeout));
    this.cancelSearches.push(cancel);
    return promise;
  }
}
